// apps/share-web/src/song-words-view.ts
//
// Страница слов ОДНОЙ песни: `/words/<id>`. Младшая сестра
// `text-share/words-view.ts` (слова СЕТЛИСТА) — но без списка. Здесь ровно
// одна песня, и читать её нечего иначе как открыв её сразу: слой чтения
// (`chart-fullscreen.ts`, тот же, что у сетлиста) и есть страница целиком, а
// не оверлей над списком. Второго рендерера слов не заводим — колонки,
// размер, тема, «вся песня на экране», ведение по темпу, секции — всё уже в
// слое, эта страница только собирает для него правильную нагрузку.
//
// СОСЕДИ (‹ ›) — только там, где страница открыта ИЗ БИБЛИОТЕКИ. У публикации
// слов нет соседей на сервере (в отличие от сетлиста, где вся страница — один
// массив песен в одной нагрузке): каждая — отдельный `song-texts/:id`, отдельный
// fetch, отдельная страница. Поэтому список соседей — не серверное поле, а
// СНИМОК того, что человек только что видел в библиотеке (отсортированное,
// отфильтрованное), пронесённый через границу настоящей навигации браузера
// (`ui/library-view.ts` вызывает `opts.navigate(path)` → `location.assign`,
// полная перезагрузка — SPA-обмена состоянием тут нет вовсе). Мост —
// `sessionStorage`: `saveWordsNeighbors` кладёт список ПЕРЕД уходом с
// библиотеки (и снова перед каждым ‹›-переходом — иначе второй шаг терял бы
// соседей), `readWordsNeighbors` его читает и СЪЕДАЕТ (одно чтение — не
// история браузера, а разовая передача). Открыли ссылку прямо, без библиотеки,
// или список успел устареть настолько, что своего id в нём уже нет, — соседей
// нет вовсе, и это НЕ отказ: `chart-fullscreen.ts` сам не рисует ‹› без
// валидного `currentIndex` в `setlist`, здесь ничего лишнего не проверяем.
//
// ‹›-ПЕРЕХОД — тоже НАСТОЯЩАЯ навигация (`deps.navigate('/words/<id>')`), а не
// подмена нагрузки на месте: у соседней песни свой fetch, своя тональность,
// своё расписание — переиспользовать смонтированный слой значило бы протащить
// уже открытую песню сквозь чужие данные. Каждый переход — новая загрузка
// страницы, ровно как первая.
//
// ИГРАТЬ ЗДЕСЬ НЕЧЕМ. `transport` слою не передаётся вовсе — аудио на этой
// поверхности нет, и кнопка, которая ничего не делает, хуже отсутствующей.
// Заперто тем же тестом, что у сетлиста.
//
// ⚠️ `bpm: 0` — «темп не задан», не ноль ударов (см. `TextShareSong`). Строка
// меты и ведение по темпу опираются на `words-model.ts` (`wordsSongs`,
// разделяемый с сетлистом код — одна и та же арифметика, а не вторая копия:
// B-260829 уже стоил ровно такого расхождения между двумя поверхностями).
//
// ⚠️ Нагрузка БЕЗ `chart` — законное состояние (публикация без текста), а не
// отказ. Слою передаётся пустой текст, и он сам рисует «слов пока нет»
// (`chart.empty`, `draw()` в chart-fullscreen.ts) — ту же пустоту, что видит
// читатель сетлиста у песни без чарта. Отдельного экрана для этого тут нет
// специально: второй рендерер слов не заводим и здесь.
//
// НЕ ЗАВЕДЕНО НАМЕРЕННО (вне мандата этой задачи): wake-lock (страница не
// держит экран телефона), лайв-бейдж (лайв — свойство СЕТЛИСТА, не одной
// публикации слов), пересчёт по смене «телефон/широкий» (`isPhone` в
// chart-fullscreen.ts v2 не участвует в потолке колонок — см. его шапку,
// «потолок один на все экраны»; держать за ним watchPhoneMode было бы
// собственным мнением без опоры на код слоя).

import './styles/chart.css';
import './styles/theme-light.css';

import { fetchSongText, type SongWordsPayload } from './song-text-api.js';
import { type WordsResolved, NETWORK_TIMEOUT_MS, defaultIsOffline } from './offline/words-resolve.js';
import { fetchMe, type MeResult } from './me.js';
import { wordsSongs, type WordsSong } from './text-share/words-model.js';
import { renderTextShareError } from './text-share/words-status.js';
import {
  mountChartFullscreen,
  type ChartFullscreenHandle,
  type ChartFullscreenDeps,
} from './player-view/chart-fullscreen.js';
import { createFullscreenSlot, type FullscreenSlot } from './player-view/fullscreen-slot.js';
import { loadReaderPrefs, saveReaderPrefs, type PrefsStore, type ReaderTheme } from './chart/reader-prefs.js';
import { createLocalClock, fmtClock } from './chart/local-clock.js';
import { setAppTheme } from './theme-boot.js';
import { sectionIndexAt } from './chart/chart-follow.js';
import { buildEmptyState } from './ui/empty-state.js';
import { t } from './ui/i18n.js';
import type { SongRow } from './ui/song-rows.js';

/**
 * Куда уходит «закрыть» у человека БЕЗ аккаунта. Литерал module-local, как у
 * пяти соседей (main.ts, ui/promo-footer.ts, ui/app-header.ts,
 * promo-content.ts, ui/login-gate.ts) — и тот же
 * лендинг, которым уже заканчивается страница слов СЕТЛИСТА, с тем же
 * доводом: «логина у её читателя нет, и „назад в библиотеку“ ему предложить
 * нечего».
 */
const LANDING_URL = 'https://bandway.app';

// ── мост соседей: библиотека → sessionStorage → эта страница ────────────────

const NEIGHBORS_KEY = 'bw.words.neighbors';

export interface WordsNeighbor { id: string; name: string }

/**
 * Строки библиотеки → соседи страницы слов, в ТОМ ЖЕ порядке. Чистая функция
 * — вызывается из `ui/library-view.ts` с уже отсортированным/отфильтрованным
 * списком (`visible` в `renderList`), то есть с порядком, который человек
 * ДЕЙСТВИТЕЛЬНО видел на экране в момент клика.
 *
 * Строки БЕЗ слов исключены: у них нет `words.id`, открыть их со страницы
 * слов нечем, а стрелка на пустую поверхность — отказ хуже отсутствия стрелки.
 */
export function neighborsFromRows(rows: readonly SongRow[]): WordsNeighbor[] {
  const out: WordsNeighbor[] = [];
  for (const r of rows) {
    if (r.words !== null) out.push({ id: r.words.id, name: r.songName });
  }
  return out;
}

/**
 * Кладёт список соседей ПЕРЕД настоящей навигацией — и с библиотеки, и с
 * каждого ‹›-перехода этой же страницы (иначе второй шаг терял бы соседей:
 * `readWordsNeighbors` съедает запись за одно чтение).
 */
export function saveWordsNeighbors(list: readonly WordsNeighbor[]): void {
  try { sessionStorage.setItem(NEIGHBORS_KEY, JSON.stringify(list)); } catch { /* приватный режим */ }
}

/**
 * Читает и СЪЕДАЕТ список соседей. Пусто, чужой формат или бросающее хранилище
 * (приватный режим) — `null`: страница открыта прямой ссылкой, и это законное
 * состояние, а не ошибка.
 */
function readWordsNeighbors(): WordsNeighbor[] | null {
  try {
    const raw = sessionStorage.getItem(NEIGHBORS_KEY);
    sessionStorage.removeItem(NEIGHBORS_KEY);
    if (raw == null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    const out: WordsNeighbor[] = [];
    for (const e of parsed) {
      if (e && typeof e === 'object' && typeof (e as { id?: unknown }).id === 'string'
        && typeof (e as { name?: unknown }).name === 'string') {
        out.push({ id: (e as { id: string }).id, name: (e as { name: string }).name });
      }
    }
    return out;
  } catch {
    return null;
  }
}

// ── мост origin: библиотека/сетлист → sessionStorage → крестик этой страницы
//
// ДЕФЕКТ 4 живой приёмки волны G — продуктовое решение владельца, дословно
// одобренный вариант: «Зашёл из вкладки Songs — вернёт туда же, в то же
// место списка. Зашёл из сетлиста — вернёт в сетлист. Поведение одинаковое
// везде и предсказуемое.» Origin запоминается В МОМЕНТ ПЕРЕХОДА
// (`ui/library-view.ts`, перед `opts.navigate('/words/' + id)`) — тот же
// приём и та же причина, что у соседей выше: угадывать его на выходе
// (`document.referrer`) ненадёжно (в установленном PWA он часто пуст, и
// офлайн — тем более), а восстановить нечего, если не записать заранее.

const ORIGIN_KEY = 'bw.words.origin';

export interface WordsOrigin {
  /** Куда ведёт крестик — ВНУТРЕННИЙ адрес, начинается с одного `/`. */
  path: string;
  /**
   * Вкладка библиотеки, которую нужно восстановить ПЕРЕД переходом — но
   * только когда `path === '/library'`: у прямого адреса сетлиста вкладки
   * нет вовсе, страница `/setlist/<id>` про библиотеку не знает ничего.
   */
  tab?: 'songs' | 'setlists';
}

/** Внутренний адрес — то же правило, что у любой навигации данными из
 *  хранилища: значение, положенное в sessionStorage кодом СТРАНИЦЫ, а не
 *  построенное прямо перед вызовом, обязано быть проверено ПЕРЕД тем, как
 *  ехать по нему. `//host/path` (протокол-относительный) тоже отклонён —
 *  браузер трактует его как внешний адрес, хотя он «начинается с /». */
function isSafeInternalPath(path: unknown): path is string {
  return typeof path === 'string' && path.startsWith('/') && !path.startsWith('//');
}

/**
 * Кладёт origin ПЕРЕД настоящей навигацией — и с библиотеки/сетлиста
 * (`ui/library-view.ts`), и с каждого ‹›-перехода этой же страницы (см.
 * `onNavigate` в `mountSongWordsView`): без повторной записи второй шаг
 * потерял бы origin ровно так же, как второй шаг терял бы соседей без
 * повторного `saveWordsNeighbors`.
 */
export function saveWordsOrigin(origin: WordsOrigin): void {
  try { sessionStorage.setItem(ORIGIN_KEY, JSON.stringify(origin)); } catch { /* приватный режим */ }
}

/**
 * Читает и СЪЕДАЕТ запись origin. Пусто, испорченная форма, внешний/
 * протокол-относительный адрес или бросающее хранилище (приватный режим) —
 * `null`: страница открыта прямой ссылкой (или запись была небезопасна), и
 * `goLibrary` идёт запасным путём — библиотека, вкладка Songs, как и до
 * этой правки.
 */
function readWordsOrigin(): WordsOrigin | null {
  try {
    const raw = sessionStorage.getItem(ORIGIN_KEY);
    sessionStorage.removeItem(ORIGIN_KEY);
    if (raw == null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const path = (parsed as { path?: unknown }).path;
    if (!isSafeInternalPath(path)) return null;
    const tab = (parsed as { tab?: unknown }).tab;
    return { path, tab: tab === 'songs' || tab === 'setlists' ? tab : undefined };
  } catch {
    return null;
  }
}

// ── страница ──────────────────────────────────────────────────────────────

export interface SongWordsViewDeps {
  /** Id публикации слов из `/words/<id>`. */
  id: string;
  apiBase?: string;
  fetchImpl?: typeof fetch;
  /** Настоящая навигация — уход со страницы целиком, не смена состояния на месте. */
  navigate: (path: string) => void;
  /** Шов для тестов: настоящая `fetchSongText` в проде. */
  fetchText?: typeof fetchSongText;
  /**
   * Волна G: «откуда взять слова» — из сети или с телефона. Решение целиком
   * в `offline/words-resolve.ts`, здесь только его исход; правил кэша эта
   * страница не содержит ни одного (ровно как `main.ts` в ветке библиотеки).
   *
   * Не задано — прежний путь: только сеть, через `fetchText`. Так работает
   * читатель без хранилища (приватный режим) и весь сьют волны C.
   */
  resolveWords?: (id: string) => Promise<WordsResolved<SongWordsPayload>>;
  /** Шов для тестов: настоящий слот в body в проде. */
  createSlot?: () => FullscreenSlot;
  /** Шов для тестов: настоящее ведение слоя в проде (см. chart-fullscreen.ts). */
  createFollow?: ChartFullscreenDeps['createFollow'];
  /** Хранилище настроек читателя. Не задано — localStorage; `null` — не хранить. */
  prefsStore?: PrefsStore | null;
  /** Часы страницы (ведение по темпу). Подмена в тестах. */
  now?: () => number;
  /**
   * Соседи по библиотеке — шов для тестов. `undefined` — читать настоящий
   * `sessionStorage` (прод, см. `readWordsNeighbors`); `null` или массив —
   * подставное значение теста, включая пустой массив («соседей нет»).
   */
  neighbors?: WordsNeighbor[] | null;
  /**
   * Откуда пришли — куда ведёт крестик (дефект 4). `undefined` — читать
   * настоящий `sessionStorage` (прод, см. `readWordsOrigin`); `null` или
   * объект — подставное значение теста. Тот же приём, что у `neighbors`
   * выше, ровно по той же причине.
   */
  origin?: WordsOrigin | null;
  /**
   * Есть ли у смотрящего аккаунт. Спрашивается ЛЕНИВО — только когда он
   * закрывает страницу, — и ровно за этим: `/library` cookie-authed и на 401
   * монтирует гейт ввода кода. Волна F впервые приводит сюда человека БЕЗ
   * аккаунта (публичные слова читаются без сессии), и «закрыть» вело его в
   * этот гейт — тупик в конце того самого сценария, ради которого волна и
   * затевалась.
   *
   * Лениво, а не на загрузке: страница слов за логином не ходит вовсе (замок
   * test/song-words-route.test.ts), и превращать её в ходящую ради выхода
   * одного читателя значило бы взять плату со всех. Ответ запоминается —
   * закрыть можно и дважды.
   *
   * Шов для тестов; в проде — `fetchMe`. Тип — `MeResult` (`me.ts`), НЕ
   * булев: дефект 3 живой приёмки волны G — владелец продукта, дословно,
   * «захожу в песню уже закешированную, нажимаю крестик, оно мне ошибку
   * выдаёт. А не список всех песен». Булев ответ схлопывал «аккаунта нет»
   * и «сеть отказала» в одно значение `false`, и оба вели на ЛЕНДИНГ — а
   * лендинг внешний и без сети недоступен всегда, то есть заведомый тупик
   * для человека, который эту страницу читает ИМЕННО потому, что она у него
   * закэширована. `askSession` ниже различает три исхода: `ok` — аккаунт
   * есть; `unauthenticated` — честно нет; иначе (включая брошенное
   * исключение) — сеть отказала, а не отрицательный ответ на вопрос.
   */
  hasSession?: () => Promise<MeResult>;
  /**
   * «Браузер считает себя офлайн?» — дефект 2 живой приёмки волны G
   * (21.09): `askSession` ниже звала `deps.hasSession`/`fetchMe` без
   * ограничения по времени, и на мёртвой сети крестик висел десятки секунд
   * — то «ничего не происходит», то «белый экран» (начатая и не
   * завершённая навигация), в зависимости от того, сколько человек успел
   * прождать. Внедряемый параметр, а не прямое чтение `navigator` в теле
   * модуля — та же причина, что у `resolveWords` (words-resolve.ts): шов
   * ради теста, а не архитектурная нужда. Не задано — `navigator.onLine
   * === false` (см. `defaultIsOffline` в words-resolve.ts, тот же
   * контракт: `false` не врёт никогда).
   */
  isOffline?: () => boolean;
}

export interface SongWordsViewHandle {
  unmount(): void;
}

function songFromPayload(payload: SongWordsPayload): WordsSong | null {
  // Обёртка в `TextSharePayload` с одной песней — переиспользование
  // `wordsSongs` (words-model.ts), а не вторая копия той же арифметики меты,
  // сетки секций и bpm:0. `v`/`name` здесь фиктивны: страница их не показывает,
  // это поля контракта СЕТЛИСТА, не одной песни.
  const songs = wordsSongs({ v: 1, name: '', songs: [payload] });
  return songs[0] ?? null;
}

export function mountSongWordsView(host: HTMLElement, deps: SongWordsViewDeps): SongWordsViewHandle {
  let unmounted = false;
  let gen = 0;

  const fetchText = deps.fetchText ?? fetchSongText;
  const apiBase = deps.apiBase ?? '';
  const fetchImpl = deps.fetchImpl ?? fetch;

  const neighbors: WordsNeighbor[] | null =
    deps.neighbors !== undefined ? deps.neighbors : readWordsNeighbors();
  const currentIndex = neighbors ? neighbors.findIndex((n) => n.id === deps.id) : -1;
  // Читается РАЗ, синхронно на монтаже — тем же приёмом, что соседи выше:
  // `goLibrary` (объявлена ниже) закрывает эту переменную, а не зовёт
  // `readWordsOrigin` заново (вторым чтением она бы уже ничего не нашла —
  // запись СЪЕДЕНА этим самым чтением).
  const origin: WordsOrigin | null =
    deps.origin !== undefined ? deps.origin : readWordsOrigin();

  const store = deps.prefsStore === undefined ? undefined : deps.prefsStore;
  const prefs = loadReaderPrefs(store);
  let columns = prefs.columns;
  let fontPct = prefs.fontPct;
  let theme: ReaderTheme = prefs.theme;
  let fit = prefs.fit;
  let follow = prefs.follow;
  let showChords = prefs.showChords;
  let showLyrics = prefs.showLyrics;
  /** Сдвиг тональности. Одна песня на всю жизнь страницы — сбрасывать не по чему,
   *  переживает переключение «ведения по темпу» (переоткрытие того же слоя). */
  let shift = 0;
  const remember = (patch: Parameters<typeof saveReaderPrefs>[0]): void => { saveReaderPrefs(patch, store); };
  // Тема — на ВСЮ страницу (в т.ч. на экраны загрузки/отказа), не только на
  // открытый слой: `<html data-theme>` общий токен, ровно как в words-view.ts.
  // Стамп ставит ОБЩИЙ владелец (theme-boot.ts): свой handle здесь означал бы
  // второго, и «Auto», выбранный на старте, перекрашивал бы страницу поверх
  // выбора, сделанного в её же настройках.
  setAppTheme(theme);
  const setTheme = (v: ReaderTheme): void => { theme = v; setAppTheme(v); remember({ theme: v }); };

  const nowMs = deps.now ?? (() => Date.now());
  const clock = createLocalClock({ now: () => nowMs() });

  const slot = (deps.createSlot ?? createFullscreenSlot)();
  let layer: ChartFullscreenHandle | null = null;
  const dropLayer = (): void => { layer?.destroy(); layer = null; };

  const mountLayer = (song: WordsSong): void => {
    dropLayer();
    // Прежний экран (загрузка/отказ) стоял в host — слой стоит в слоте body,
    // и без этого он остался бы висеть под инертной обложкой (slot.cover()
    // накрывает body, но host не трогает), лишний узел в дереве навсегда.
    host.textContent = '';
    const clockAvailable = song.bpm > 0 && song.sections.some((x) => (x.name ?? '').trim() !== '');
    const hasClock = follow && clockAvailable;
    // Часы заводятся РОВНО раз на песню (индекс фиктивный — здесь она одна):
    // переоткрытие слоя при переключении «ведения» не должно сбрасывать
    // позицию, уже отсчитанную часами.
    if (clock.songIdx() !== 0) clock.arm(0, song.durationSec);
    slot.cover();
    layer = mountChartFullscreen(slot.el, {
      text: song.text,
      links: song.links,
      songName: song.name,
      songKey: song.key,
      songMeta: song.meta,
      bpm: song.bpm,
      timeSignature: song.timeSignature,
      // Расписание едет слою РОВНО когда его ведут локальные часы — та же
      // причина, что в words-view.ts: без активного источника позиции лента
      // и ряд «сейчас → дальше» стояли бы дырой из двух черт.
      sections: hasClock ? song.sections : [],
      setlist: (neighbors ?? []).map((n) => ({ songName: n.name })),
      currentIndex,
      // v2: потолок колонок один на все экраны (chart-fullscreen.ts, шапка
      // ChartFullscreenDeps.isPhone) — значение ни на что не влияет.
      isPhone: false,
      position: { now: () => (hasClock ? clock.now() : null) },
      onNavigate: (at) => {
        const list = neighbors ?? [];
        const nb = list[at];
        if (!nb) return;
        // Соседей и origin кладём ЗАНОВО перед каждым переходом — иначе на
        // втором шаге читать было бы уже нечего (readWordsNeighbors/
        // readWordsOrigin съедают запись). Origin переносится БЕЗ изменений
        // — ‹›-шаг не меняет то, откуда человек изначально зашёл.
        saveWordsNeighbors(list);
        if (origin) saveWordsOrigin(origin);
        deps.navigate('/words/' + encodeURIComponent(nb.id));
      },
      // Вкладку Songs ставит `goLibrary` внутри `leave` — та же запись, что
      // делает сама библиотека при переключении вкладки (ui/library-view.ts,
      // 'bw.lib.tab'): без неё возврат мог попасть на Setlists, если он был
      // выбран раньше. У читателя без аккаунта её ставить не за чем —
      // библиотеки, в которую он бы вернулся, у него нет.
      onClose: () => { leave(); },
      initialShift: shift,
      onShiftChange: (n) => { shift = n; },
      initialColumns: columns,
      onColumnsChange: (n) => { columns = n; remember({ columns: n }); },
      initialFontPct: fontPct,
      onFontPctChange: (n) => { fontPct = n; remember({ fontPct: n }); },
      theme, fit, follow, showChords, showLyrics,
      followAvailable: clockAvailable,
      onThemeChange: (v) => setTheme(v),
      onFitChange: (on) => { fit = on; remember({ fit: on }); },
      onFollowChange: (on) => {
        follow = on; remember({ follow: on });
        if (!on) clock.pause();
        // Пилюля и расписание монтируются вместе со слоем — пересобрать на
        // той же песне (лист один, но клоки/секции — часть монтажа).
        if (clockAvailable) mountLayer(song);
      },
      onShowChordsChange: (on) => { showChords = on; remember({ showChords: on }); },
      onShowLyricsChange: (on) => { showLyrics = on; remember({ showLyrics: on }); },
      onJumpSection: (i) => { if (hasClock) { const sec = song.sections[i]; if (sec) clock.seek(sec.startSec); } },
      followPill: hasClock ? {
        isPlaying: () => clock.isPlaying(),
        toggle: () => { if (clock.isPlaying()) clock.pause(); else clock.start(); },
        label: () => {
          const pos = clock.posSec();
          const si = sectionIndexAt(song.sections, pos);
          const name = si >= 0 ? (song.sections[si].name ?? '').trim() : '';
          return name ? `${fmtClock(pos)} · ${name}` : fmtClock(pos);
        },
      } : undefined,
      createFollow: deps.createFollow,
      // transport НЕ передаётся: играть здесь нечем — см. шапку модуля.
    });
  };

  const renderState = (node: HTMLElement): void => {
    host.textContent = '';
    host.appendChild(node);
  };

  const showLoading = (): void => {
    renderState(buildEmptyState({ icon: 'music', title: t('songWords.loading'), dataRole: 'song-words-loading' }));
  };

  /**
   * ДЕФЕКТ 4: адрес и вкладка приезжают из `origin` (записан на входе,
   * см. выше) — не «библиотека, Songs» намертво. Нет записи, она
   * нечитаема или небезопасна (`readWordsOrigin` уже это проверила) —
   * прежний путь, ровно как до этой правки.
   */
  const goLibrary = (): void => {
    const path = origin?.path ?? '/library';
    // Вкладку восстанавливаем ТОЛЬКО когда действительно возвращаемся в
    // библиотеку: адрес сетлиста (`/setlist/<id>`) её не читает вовсе, и
    // запись здесь была бы лишней — молча пережила бы следующий честный
    // заход в библиотеку с другого таба.
    if (path === '/library') {
      const tab = origin?.tab ?? 'songs';
      try { localStorage.setItem('bw.lib.tab', tab); } catch { /* приватный режим */ }
    }
    deps.navigate(path);
  };

  /** Три исхода вопроса «есть ли аккаунт», а не два — дефект 3 живой
   *  приёмки волны G (см. doc-комментарий `hasSession` выше). Один на жизнь
   *  страницы, как и раньше. */
  type SessionAnswer = 'authed' | 'anon' | 'network-error';
  const isOffline = deps.isOffline ?? defaultIsOffline;
  let sessionAnswer: Promise<SessionAnswer> | null = null;
  const askSession = (): Promise<SessionAnswer> => {
    if (!sessionAnswer) {
      // ДЕФЕКТ 2 живой приёмки волны G: браузер САМ сказал, что сети нет —
      // спрашивать `/api/me` означало бы висеть десятки секунд ради
      // гарантированного отказа (см. doc-комментарий `isOffline` в
      // SongWordsViewDeps). ⚠️ ЗАМОК: убрать эту ветку — красит тест
      // «браузер офлайн — network-error немедленно, hasSession НЕ
      // вызывается»: он проверяет, что `hasSession` не был позван, а не
      // только итоговый ответ.
      if (isOffline()) {
        sessionAnswer = Promise.resolve<SessionAnswer>('network-error');
      } else {
        const check = deps.hasSession ? deps.hasSession() : fetchMe(apiBase, fetchImpl);
        const answered = check
          .then((r): SessionAnswer => (r.ok ? 'authed' : r.unauthenticated ? 'anon' : 'network-error'))
          // Брошенное исключение — тот же сетевой отказ, что и `{ok:false}` без
          // `unauthenticated` выше, НЕ «аккаунта нет»: раньше оба схлопывались
          // сюда одинаково (`.catch(() => false)`), и это и было дефектом 3.
          .catch((): SessionAnswer => 'network-error');
        // Браузер СЧИТАЕТ себя онлайн — тот самый случай, когда он может
        // врать (зальный Wi-Fi с мёртвым аплинком). `check` не обязана
        // отказать быстро на мёртвом аплинке, поэтому ожидание ограничено
        // тем же приёмом и той же величиной, что у `resolveWords`
        // (words-resolve.ts) — оба решают один и тот же вопрос: сколько
        // терпеть перед тем, как поверить, что сети правда нет.
        // ⚠️ ЗАМОК: убрать таймер (ждать `answered` безусловно) — красит
        // тест «hasSession виснет навсегда» (fake timers).
        const TIMED_OUT = Symbol('askSession-timeout');
        let timeoutHandle: ReturnType<typeof setTimeout>;
        const timeout = new Promise<typeof TIMED_OUT>((resolve) => {
          timeoutHandle = setTimeout(() => resolve(TIMED_OUT), NETWORK_TIMEOUT_MS);
        });
        sessionAnswer = Promise.race([answered, timeout]).then((v) => {
          clearTimeout(timeoutHandle);
          return v === TIMED_OUT ? 'network-error' : v;
        });
      }
    }
    return sessionAnswer;
  };

  /**
   * «Закрыть» ведёт ТУДА, КУДА ЕСТЬ ХОД.
   *
   * `anon` (честный отказ сервера) — у человека без аккаунта библиотеки не
   * существует: `/library` ответит 401 и покажет гейт ввода кода, которого у
   * него нет, — поэтому лендинг, тот же и по тому же доводу, что у страницы
   * слов сетлиста.
   *
   * `authed` И `network-error` — ОБА ведут в библиотеку. Довод для
   * `network-error` (дефект 3): смотрящий эту страницу СЕЙЧАС, офлайн, уже
   * прошёл через `/library`, иначе слов в кэше (`res.source === 'cache'` —
   * см. `load` выше) у него не было бы вовсе —
   * страница слов кэша не заводит сама, кэш кладёт докачка волны G после
   * успешного захода в библиотеку. `/library` офлайн поднимается из
   * собственного кэша (сервис-воркер — дефект 1 той же приёмки, IndexedDB —
   * дефект 2) и покажет список; ЛЕНДИНГ ВНЕШНИЙ и офлайн НЕДОСТУПЕН ВСЕГДА —
   * заведомый тупик именно там, где волна обещала офлайн работать. Кэш `me`
   * (дефект 2, offline/offline-store.ts) здесь намеренно НЕ читается ещё
   * раз: без него `/library` всё равно не тупик (пустой список с ретраем,
   * не браузерная ошибка), а с ним — та же самая библиотека, только с
   * лишним обращением к хранилищу ради решения, которое и без него
   * однозначно.
   */
  const leave = (): void => {
    void askSession().then((answer) => {
      if (answer === 'anon') { deps.navigate(LANDING_URL); return; }
      goLibrary();
    });
  };

  const showForbidden = (): void => {
    renderState(buildEmptyState({
      icon: 'users',
      title: t('songWords.forbiddenTitle'),
      body: t('songWords.forbiddenBody'),
      dataRole: 'song-words-forbidden',
      action: { label: t('common.backToLibrary'), onClick: () => goLibrary() },
    }));
  };

  const showGone = (): void => {
    renderState(buildEmptyState({
      icon: 'musicOff',
      title: t('songWords.goneTitle'),
      body: t('songWords.goneBody'),
      dataRole: 'song-words-gone',
      action: { label: t('common.backToLibrary'), onClick: () => goLibrary() },
    }));
  };

  /** Прежний путь: только сеть. Кэша у страницы нет, пока его не дали. */
  const resolveNetworkOnly = async (id: string): Promise<WordsResolved<SongWordsPayload>> => {
    const r = await fetchText(id, apiBase, fetchImpl);
    return r.ok ? { source: 'network', payload: r.payload } : { source: 'none', reason: r.reason };
  };
  const resolve = deps.resolveWords ?? resolveNetworkOnly;

  const load = async (): Promise<void> => {
    const myGen = ++gen;
    showLoading();
    // Второй эшелон: `resolve` сам по себе не обязан бросать (offline/
    // words-resolve.ts глотает отказы записи/стирания кэша), но у решения
    // есть свои зависимости (хранилище, снимок библиотеки), и ЛЮБОЙ их
    // будущий необработанный отказ не имеет права оставить человека на
    // «Loading…» навсегда. У экрана уже есть состояние ошибки для этого —
    // тот же путь, что у отказа сети, с кнопкой «ещё раз»: честный отказ
    // лучше вечного спиннера.
    let res: WordsResolved<SongWordsPayload>;
    try {
      res = await resolve(deps.id);
    } catch {
      if (unmounted || myGen !== gen) return;
      renderTextShareError(host, { onRetry: () => { void load(); } });
      return;
    }
    if (unmounted || myGen !== gen) return;
    if (res.source !== 'none') {
      const song = songFromPayload(res.payload);
      if (!song) {
        renderState(buildEmptyState({
          icon: 'alert', title: t('errorScreen.loadFailedTitle'), body: t('errorScreen.loadFailedBody'),
          dataRole: 'song-words-error',
        }));
        return;
      }
      mountLayer(song);
      return;
    }
    if (res.reason === 'auth') { deps.navigate('/login'); return; }
    if (res.reason === 'forbidden') { showForbidden(); return; }
    if (res.reason === 'gone') { showGone(); return; }
    // 'network' — сеть или сервер: слова могут появиться со второй попытки,
    // поэтому кнопка «ещё раз», а не выход. Тот же экран, что у сетлиста.
    renderTextShareError(host, { onRetry: () => { void load(); } });
  };

  void load();

  return {
    unmount(): void {
      unmounted = true;
      // Тема переживает страницу — она свойство человека (theme-boot.ts).
      dropLayer();
      slot.destroy();
      host.textContent = '';
    },
  };
}
